import { formatTime } from '@/filters';
import { formatAccuracyDistanceMeters } from '@/utils/format-accuracy-distance';
import { fixTypeLabelFromCompactCode } from '@/utils/fix-type-compact-code';
import {
  trackPopupBoltIconSvg,
  trackPopupClockIconSvg,
  trackPopupCrosshairIconSvg,
  trackPopupFixTypeIconSvg,
} from '@/utils/track-vertex-popup-icons';

export type MapTelemetryPopupFacts = {
  timestampSec?: number | null;
  battery?: number | null;
  /** null / omitted → metric not drawn. */
  fixTypeCode?: number | null;
  /** null / omitted → metric not drawn. */
  accuracyMeters?: number | null;
};

export type MapTelemetryPopupRowOptions = {
  /**
   * When true, wrap the time line in `__time` (track vertex).
   * Live position uses a separate title and leaves time unemphasized.
   */
  emphasizeTime?: boolean;
};

export function escapeMapPopupHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function mapPopupRowHtml(
  iconSvg: string,
  valueHtml: string,
  muted = false,
): string {
  const valueClass = muted
    ? 'gpx-track-vertex-popup__value gpx-track-vertex-popup__value--muted'
    : 'gpx-track-vertex-popup__value';
  return `<div class="gpx-track-vertex-popup__row"><span class="gpx-track-vertex-popup__icon">${iconSvg}</span><span class="${valueClass}">${valueHtml}</span></div>`;
}

function metricHtml(
  iconSvg: string,
  valueHtml: string,
  muted = false,
): string {
  const valueClass = muted
    ? 'gpx-track-vertex-popup__value gpx-track-vertex-popup__value--muted'
    : 'gpx-track-vertex-popup__value';
  return `<span class="gpx-track-vertex-popup__metric"><span class="gpx-track-vertex-popup__icon">${iconSvg}</span><span class="${valueClass}">${valueHtml}</span></span>`;
}

/** Battery + fix + accuracy as one compact row (order: battery, fix, accuracy). */
function metricsRowHtml(facts: MapTelemetryPopupFacts): string | null {
  const { battery, fixTypeCode, accuracyMeters } = facts;
  const metrics: string[] = [];

  if (battery !== null && battery !== undefined) {
    metrics.push(
      metricHtml(
        trackPopupBoltIconSvg(),
        `<span class="gpx-track-vertex-popup__battery">${escapeMapPopupHtml(String(battery))}%</span>`,
      ),
    );
  }

  if (fixTypeCode !== null && fixTypeCode !== undefined) {
    const fixLabel = fixTypeLabelFromCompactCode(fixTypeCode);
    metrics.push(
      metricHtml(
        trackPopupFixTypeIconSvg(fixTypeCode),
        `<span class="gpx-track-vertex-popup__fix">${escapeMapPopupHtml(fixLabel)}</span>`,
      ),
    );
  }

  if (accuracyMeters !== null && accuracyMeters !== undefined) {
    if (accuracyMeters > 0) {
      const accuracyLabel = formatAccuracyDistanceMeters(accuracyMeters);
      metrics.push(
        metricHtml(
          trackPopupCrosshairIconSvg(),
          `<span class="gpx-track-vertex-popup__accuracy">${escapeMapPopupHtml(accuracyLabel)}</span>`,
        ),
      );
    } else {
      metrics.push(
        metricHtml(
          trackPopupCrosshairIconSvg(),
          escapeMapPopupHtml('Unknown accuracy'),
          true,
        ),
      );
    }
  }

  if (metrics.length === 0) return null;
  return `<div class="gpx-track-vertex-popup__row gpx-track-vertex-popup__metrics">${metrics.join('')}</div>`;
}

/** Shared time + compact battery/fix/accuracy rows for map popups. */
export function mapTelemetryPopupRows(
  facts: MapTelemetryPopupFacts,
  options: MapTelemetryPopupRowOptions = {},
): string[] {
  const { timestampSec } = facts;
  const emphasizeTime = options.emphasizeTime === true;
  const rows: string[] = [];

  if (typeof timestampSec === 'number' && timestampSec > 0) {
    const line = formatTime(
      new Date(timestampSec * 1000),
      'EEE, MMM d, yyyy · h:mm a',
    );
    if (line) {
      const valueHtml = emphasizeTime
        ? `<span class="gpx-track-vertex-popup__time">${escapeMapPopupHtml(line)}</span>`
        : escapeMapPopupHtml(line);
      rows.push(mapPopupRowHtml(trackPopupClockIconSvg(), valueHtml));
    }
  }

  const metrics = metricsRowHtml(facts);
  if (metrics) rows.push(metrics);

  return rows;
}
